import { useCallback, useRef, useState } from "react";

import { Flex } from "@radix-ui/themes";
import {
  Document,
  Page,
  PageProps as ReactPdfPageProps,
  pdfjs,
} from "react-pdf";
import { OnDocumentLoadSuccess } from "react-pdf/dist/cjs/shared/types";

import { DocumentContainer } from "./DocumentContainer";
import { DocumentToolbar } from "./DocumentToolbar";
import { Spinner } from "../Spinner";

import "react-pdf/dist/Page/TextLayer.css";
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export type PageProps = ReactPdfPageProps;

type Props = {
  src?: string;
  onLoadSuccess?: OnDocumentLoadSuccess;
  defaultScale?: number;
  defaultPage?: number;
};

type DocumentState = {
  totalPages: number;
  currentPage: number;
  scale: number;
};

const makePages = (document: DocumentState): PageProps[] => {
  const pages: PageProps[] = [];
  for (let i = 0; i < document.totalPages; i++) {
    pages.push({
      pageNumber: i + 1,
      scale: document.scale,
      renderAnnotationLayer: false,
      renderTextLayer: true,
    });
  }
  return pages;
};

export const PDFViewer = ({ src, defaultScale, defaultPage }: Props) => {
  const [documentState, setDocumentState] = useState<DocumentState>({
    totalPages: 0,
    currentPage: defaultPage ?? 1,
    scale: defaultScale ?? 1,
  });

  const pageRefs = useRef<{
    [key: number]: HTMLCanvasElement;
  }>({});

  const executeScroll = useCallback((pageNumber: number) => {
    const element = pageRefs.current[pageNumber];
    if (element) {
      element.scrollIntoView();
    }
  }, []);

  const handlePageChange = (newPage: number) => {
    setDocumentState({
      ...documentState,
      currentPage: newPage,
    });
    executeScroll(newPage);
  };

  const handleZoomIn = () => {
    setDocumentState({
      ...documentState,
      scale: documentState.scale + 0.1,
    });
    pageRefs.current = {};
  };

  const handleZoomOut = () => {
    setDocumentState({
      ...documentState,
      scale: documentState.scale - 0.1,
    });
    pageRefs.current = {};
  };

  return (
    <Flex direction="column" width="100%">
      <DocumentToolbar
        zoomLevel={documentState?.scale * 100}
        totalPages={documentState?.totalPages}
        currentPage={documentState?.currentPage}
        onPageChange={handlePageChange}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
      />
      <DocumentContainer>
        <Document
          file={src}
          onLoadSuccess={(document) => {
            setDocumentState({
              ...documentState,
              totalPages: document.numPages,
            });
            setTimeout(() => executeScroll(documentState.currentPage), 250);
          }}
          loading={<Spinner size="xl" />}
        >
          <Flex gap="2" direction="column">
            {makePages(documentState).map((page, i) => (
              <Page
                key={`page-${i}`}
                {...page}
                canvasRef={(element) => {
                  if (element && !pageRefs.current[i + 1])
                    pageRefs.current[i + 1] = element;
                }}
              />
            ))}
          </Flex>
        </Document>
      </DocumentContainer>
    </Flex>
  );
};
